import type { ReactNode } from "react";

/**
 * Inline line icons in the Plex nav idiom: thin rounded strokes that inherit
 * color via `currentColor`, so a nav item's text-color transition (muted →
 * tungsten → vermilion on active) carries the icon along for free. No icon
 * dependency — keeps the app's token-driven, dependency-light styling.
 */
type IconProps = {
  className?: string;
  size?: number;
  /** Heavier stroke (2.25 vs 1.75) — the set is outline-only, so an "active"
   * glyph is drawn bolder in the accent rather than swapped for a filled twin
   * (the top-bar activity button when streams are playing). */
  bold?: boolean;
};

function Icon({
  size = 20,
  className,
  bold = false,
  children,
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={bold ? 2.25 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function HomeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 10.5 12 3.5l8.5 7" />
      <path d="M5.5 9.2V20h13V9.2" />
      <path d="M9.75 20v-5.5h4.5V20" />
    </Icon>
  );
}

// Discover — compass, echoing Plex's circle-with-needle mark.
export function CompassIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M15.5 8.5 13.2 13.2 8.5 15.5 10.8 10.8z" />
    </Icon>
  );
}

// Watchlist — bookmark ribbon.
export function BookmarkIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 3.75h12a1 1 0 0 1 1 1V20.5l-7-4-7 4V4.75a1 1 0 0 1 1-1z" />
    </Icon>
  );
}

// Library — filmstrip, echoing Plex's "Movies & Shows" mark.
export function FilmstripIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="4" width="17" height="16" rx="1.6" />
      <path d="M8 4v16M16 4v16" />
      <path d="M3.5 8h4.5M3.5 12h4.5M3.5 16h4.5M16 8h4.5M16 12h4.5M16 16h4.5" />
    </Icon>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.75-3.75" />
    </Icon>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 6.5h16M4 12h16M4 17.5h16" />
    </Icon>
  );
}

export function GearIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5.5v13M5.5 12h13" />
    </Icon>
  );
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 9.5l6 6 6-6" />
    </Icon>
  );
}

// Search source filter — two sliders (Plex uses this for "Sources to Search").
export function SlidersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7.5h4M13 7.5h7" />
      <circle cx="10.5" cy="7.5" r="2.25" />
      <path d="M4 16.5h9M18 16.5h2" />
      <circle cx="15.5" cy="16.5" r="2.25" />
    </Icon>
  );
}

// Settings: Account — head and shoulders.
export function UserIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.75 20.25a7.25 7.25 0 0 1 14.5 0" />
    </Icon>
  );
}

// Settings: Sharing — two figures, the front one leading.
export function UsersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="9" cy="8.25" r="3.25" />
      <path d="M2.75 19.5a6.25 6.25 0 0 1 12.5 0" />
      <path d="M15.5 5.4a3.25 3.25 0 0 1 0 5.7M17.6 13.9a6.26 6.26 0 0 1 3.65 5.6" />
    </Icon>
  );
}

// Settings: Devices — monitor on a stand.
export function MonitorIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="4.5" width="18" height="12.5" rx="1.6" />
      <path d="M9.5 20.5h5M12 17v3.5" />
    </Icon>
  );
}

// Settings: Security — shield.
export function ShieldIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3.25 5 6v5.5c0 4.4 3 7.6 7 9.25 4-1.65 7-4.85 7-9.25V6z" />
    </Icon>
  );
}

// Settings: Network — globe with meridian.
export function GlobeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.6 2.3 3.9 5.15 3.9 8.5s-1.3 6.2-3.9 8.5c-2.6-2.3-3.9-5.15-3.9-8.5s1.3-6.2 3.9-8.5z" />
    </Icon>
  );
}

// Server selector — two stacked rack units.
export function ServerIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="4.5" width="17" height="6.5" rx="1.4" />
      <rect x="3.5" y="13" width="17" height="6.5" rx="1.4" />
      <path d="M7 7.75h.01M7 16.25h.01" />
    </Icon>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 12.5 10 18 19.5 6.5" />
    </Icon>
  );
}

export function TvIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.75" y="6.5" width="18.5" height="12" rx="1.75" />
      <path d="M8.5 3.5 12 6.5l3.5-3" />
    </Icon>
  );
}

// Security › Sessions — laptop: a screen over its wider base.
export function LaptopIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="5" width="16" height="11" rx="1.6" />
      <path d="M2.5 18.75h19" />
    </Icon>
  );
}

// Security › Sessions — phone: a tall slab with a home bar.
export function PhoneIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="7" y="2.75" width="10" height="18.5" rx="2" />
      <path d="M11 18.25h2" />
    </Icon>
  );
}

// Security › Sessions — tablet: the phone's slab, wider.
export function TabletIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4.5" y="3" width="15" height="18" rx="2" />
      <path d="M11 18h2" />
    </Icon>
  );
}

export function FolderIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.25 6.75A1.5 1.5 0 0 1 4.75 5.25h4l2 2.5h6.5a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5H4.75a1.5 1.5 0 0 1-1.5-1.5z" />
    </Icon>
  );
}

export function DatabaseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <ellipse cx="12" cy="6" rx="7.25" ry="2.75" />
      <path d="M4.75 6v12c0 1.5 3.25 2.75 7.25 2.75s7.25-1.25 7.25-2.75V6" />
      <path d="M4.75 12c0 1.5 3.25 2.75 7.25 2.75s7.25-1.25 7.25-2.75" />
    </Icon>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9.5 5.5 16 12l-6.5 6.5" />
    </Icon>
  );
}

export function ArrowUpIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 19V5.5" />
      <path d="M6 11.5 12 5.5l6 6" />
    </Icon>
  );
}

// Save File / Download converted… — an arrow down onto a tray line.
export function DownloadIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5v13" />
      <path d="M6 12.5l6 6 6-6" />
      <path d="M5 20h14" />
    </Icon>
  );
}

export function DotsVerticalIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5.5h.01M12 12h.01M12 18.5h.01" />
    </Icon>
  );
}

export function PlayIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7.5 4.75 19 12 7.5 19.25z" />
    </Icon>
  );
}

export function GaugeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 19a9 9 0 1 1 15 0" />
      <path d="m12 13 3.5-4.5" />
      <circle cx="12" cy="13.5" r="1" />
    </Icon>
  );
}

export function ChipIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="6.5" y="6.5" width="11" height="11" rx="1.5" />
      <rect x="10" y="10" width="4" height="4" rx="0.5" />
      <path d="M9.5 6.5v-3M14.5 6.5v-3M9.5 20.5v-3M14.5 20.5v-3M6.5 9.5h-3M6.5 14.5h-3M20.5 9.5h-3M20.5 14.5h-3" />
    </Icon>
  );
}

export function PulseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 12h4l2.5-6 4 12L16 12h5" />
    </Icon>
  );
}

export function TerminalIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="4.5" width="18" height="15" rx="2" />
      <path d="m7 9.5 3 2.75L7 15M12.5 15H17" />
    </Icon>
  );
}

// ---- player glyphs ----

export function PauseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 5v14M15 5v14" />
    </Icon>
  );
}

export function StopIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="6.5" y="6.5" width="11" height="11" rx="1.5" />
    </Icon>
  );
}

export function MessageIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 14.5a2 2 0 0 1-2 2H7.5l-4 3.5v-14a2 2 0 0 1 2-2H18a2 2 0 0 1 2 2z" />
    </Icon>
  );
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m14 6-6 6 6 6" />
    </Icon>
  );
}

// Skip arcs: open circle with an arrowhead; the numeral is fill-text —
// digits cannot be strokes at this size (documented idiom exception,
// consistent between the pair).
export function SkipBack10Icon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 4.5a7.5 7.5 0 1 1-7.28 5.7" />
      <path d="M4.5 4.75v5h5" fill="none" />
      <text
        x="12"
        y="15"
        textAnchor="middle"
        fontSize="8"
        fontFamily="var(--rago-font-sans)"
        fontWeight="600"
        fill="currentColor"
        stroke="none"
      >
        10
      </text>
    </Icon>
  );
}

export function SkipForward30Icon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 4.5a7.5 7.5 0 1 0 7.28 5.7" />
      <path d="M19.5 4.75v5h-5" fill="none" />
      <text
        x="12"
        y="15"
        textAnchor="middle"
        fontSize="8"
        fontFamily="var(--rago-font-sans)"
        fontWeight="600"
        fill="currentColor"
        stroke="none"
      >
        30
      </text>
    </Icon>
  );
}

/** Auto Play (next episode): the skip arc with a play triangle where the
 * digits sit — same family as the ±10/30 glyphs, read at 16px in a menu. */
export function AutoPlayIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 4.5a7.5 7.5 0 1 0 7.28 5.7" />
      <path d="M19.5 4.75v5h-5" fill="none" />
      <path d="M10.25 9.25v5.5l4.5-2.75z" />
    </Icon>
  );
}

export function PrevEpisodeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6.5 5.5v13" />
      <path d="M18 6.5 9.5 12l8.5 5.5z" />
    </Icon>
  );
}

export function NextEpisodeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M17.5 5.5v13" />
      <path d="M6 6.5 14.5 12 6 17.5z" />
    </Icon>
  );
}

export function VolumeHighIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 9.5v5h3.5L12 18.5v-13L7.5 9.5z" />
      <path d="M15.5 9a4.2 4.2 0 0 1 0 6" />
      <path d="M18 6.5a8 8 0 0 1 0 11" />
    </Icon>
  );
}

export function VolumeLowIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 9.5v5h3.5L12 18.5v-13L7.5 9.5z" />
      <path d="M15.5 9a4.2 4.2 0 0 1 0 6" />
    </Icon>
  );
}

export function VolumeMuteIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 9.5v5h3.5L12 18.5v-13L7.5 9.5z" />
      <path d="m15.5 9.5 5 5M20.5 9.5l-5 5" />
    </Icon>
  );
}

export function CaptionsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M10.5 10.4a2.1 2.1 0 1 0 0 3.2M17 10.4a2.1 2.1 0 1 0 0 3.2" />
    </Icon>
  );
}

export function PipIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M21 8.5V6.5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h5" />
      <rect x="13" y="12.5" width="8" height="7" rx="1.5" />
    </Icon>
  );
}

export function ExpandIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 4.5H4.5V9M15 4.5h4.5V9M9 19.5H4.5V15M15 19.5h4.5V15" />
    </Icon>
  );
}

export function CollapseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 9H9V4.5M19.5 9H15V4.5M4.5 15H9v4.5M19.5 15H15v4.5" />
    </Icon>
  );
}

// Audio track picker — sound bars (distinct from the volume speaker).
export function AudioTrackIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 10v4M9 7v10M13 9.5v5M17 5.5v13M21 8.5v7" />
    </Icon>
  );
}

/** Chapters: a filmstrip lane with three chapter marks. */
export function ChaptersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M8 6v12M13.5 6v12M3 12h18" />
    </Icon>
  );
}

export function KeyboardIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="6.5" width="18" height="11" rx="2" />
      <path d="M6.5 10h.01M10 10h.01M13.5 10h.01M17 10h.01M6.5 13h.01M17 13h.01M9 14.5h6" />
    </Icon>
  );
}

export function MegaphoneIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m4 10.5 16-5.5v14l-16-5.5z" />
      <path d="M4 10.5v3.5" />
      <path d="M12.5 17.2a3 3 0 1 1-5.7-1.9" />
    </Icon>
  );
}

export function TagIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 4h6.6a2 2 0 0 1 1.4.6l7.4 7.4a2 2 0 0 1 0 2.8L14.8 19.4a2 2 0 0 1-2.8 0L4.6 12a2 2 0 0 1-.6-1.4V4z" />
      <path d="M8.5 8.5h.01" />
    </Icon>
  );
}

export function WarningTriangleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10.3 5.1 3 17.6a2 2 0 0 0 1.7 3h14.6a2 2 0 0 0 1.7-3L13.7 5.1a2 2 0 0 0-3.4 0z" />
      <path d="M12 10v4" />
      <path d="M12 17.2h.01" />
    </Icon>
  );
}

export function XCircleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m9.5 9.5 5 5M14.5 9.5l-5 5" />
    </Icon>
  );
}

export function TrashIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 7h15" />
      <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
      <path d="m6.5 7 .8 12.1a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
      <path d="M10 11v5.5M14 11v5.5" />
    </Icon>
  );
}

// Settings › Account: a well's Edit — pencil, with the ferrule's line.
export function PencilIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M16.5 4.5a2.12 2.12 0 0 1 3 3L8 19l-4 1 1-4z" />
      <path d="m14.5 6.5 3 3" />
    </Icon>
  );
}

// Settings rail: Troubleshooting — wrench.
export function WrenchIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </Icon>
  );
}

// Account menu: sign out — door with an outbound arrow.
export function SignOutIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10 4H6.5A1.5 1.5 0 0 0 5 5.5v13A1.5 1.5 0 0 0 6.5 20H10" />
      <path d="M14 8l4 4-4 4M18 12H9.5" />
    </Icon>
  );
}

// Settings rail: Scheduled Tasks — clock.
export function ClockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </Icon>
  );
}

// Dismiss / close — dialogs, removable rows, clearable inputs.
export function XIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
    </Icon>
  );
}

// Row "See all" affordance — a true arrow (chevrons stay for paging).
export function ArrowRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 12h15M13.5 6l6 6-6 6" />
    </Icon>
  );
}

// Detail actions: play again from the start — counter-clockwise restart.
export function RestartIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 4.5v4.6h4.6" />
      <path d="M5.4 14.7a7.6 7.6 0 1 0 .5-6.6L4.5 9.1" />
    </Icon>
  );
}

// Item menu: Refresh Metadata — clockwise arrows.
export function RefreshIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M19.5 4.5v4.6h-4.6" />
      <path d="M18.6 9.1a7.6 7.6 0 1 0 .5 6.6" />
    </Icon>
  );
}

// Settings › Trakt: two chain links — a connected outside service.
export function LinkIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10.2 13.8a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
      <path d="M13.8 10.2a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
    </Icon>
  );
}

// "Open Trakt": the link leaves for another site, in a new tab.
export function ArrowUpRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7 17 17 7M9 7h8v8" />
    </Icon>
  );
}

// Copy to the clipboard (the claim code's button): two squares, one behind.
export function CopyIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4.5A1.5 1.5 0 0 1 3 13.5v-9A1.5 1.5 0 0 1 4.5 3h9A1.5 1.5 0 0 1 15 4.5V5" />
    </Icon>
  );
}

// Auth screens: a field's error line — circled "!".
export function AlertCircleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 7.8v5M12 16v.1" />
    </Icon>
  );
}

// Forgot password: the "Sent to" tile — an envelope.
export function MailIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
      <path d="m4 7 8 6 8-6" />
    </Icon>
  );
}

// Item menu: Get Info — circled i.
export function InfoIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 11v5M12 8.2v.1" />
    </Icon>
  );
}

// Ratings: the TMDB community-score star.
export function StarIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 4.2l2.32 4.83 5.32.69-3.9 3.7.99 5.27L12 16.15l-4.73 2.54.99-5.27-3.9-3.7 5.32-.69z" />
    </Icon>
  );
}

/** Password reveal toggle (components/password-input.tsx). */
export function EyeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2.5 12s3.5-6.5 9.5-6.5S21.5 12 21.5 12s-3.5 6.5-9.5 6.5S2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  );
}

export function EyeOffIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 3.5l17 17" />
      <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
      <path d="M9.9 5.8A10 10 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a16 16 0 0 1-3 3.9" />
      <path d="M6.3 6.3A15.5 15.5 0 0 0 2.5 12s3.5 6.5 9.5 6.5c1.5 0 2.8-.4 4-1" />
    </Icon>
  );
}

export function SortIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M8 4.5v15" />
      <path d="M4.5 8 8 4.5 11.5 8" />
      <path d="M16 19.5v-15" />
      <path d="M12.5 16 16 19.5l3.5-3.5" />
    </Icon>
  );
}

export function ChevronUpIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 14.5l6-6 6 6" />
    </Icon>
  );
}

export function ShuffleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7h3.5c1.5 0 2.9.7 3.8 1.9l3.9 5.2c.9 1.2 2.3 1.9 3.8 1.9H20" />
      <path d="M4 17h3.5c1.5 0 2.9-.7 3.8-1.9l.7-.9" />
      <path d="m14.2 9 .8-1.1c.9-1.2 2.3-1.9 3.8-1.9H20" />
      <path d="M17.5 3.5 20 6l-2.5 2.5" />
      <path d="M17.5 14.5 20 17l-2.5 2.5" />
    </Icon>
  );
}
